import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, Mail, Phone, Package, Calendar, Download, ExternalLink, AlertCircle, Search, Building2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

const STATUS_LABELS = {
    received: "Ricevuto",
    draft: "In bozza",
    production: "In produzione",
    ready: "Pronto",
    delivered: "Consegnato",
};

const PRIORITY_LABELS = {
    normal: { label: "Normale", color: "#64748B" },
    high: { label: "Alta", color: "#F59E0B" },
    urgent: { label: "Urgente", color: "#EF4444" },
};

function fmtDate(iso) {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" });
    } catch { return iso; }
}

function bytesToReadable(b) {
    if (!b) return "—";
    if (b < 1024) return `${b} B`;
    if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
    return `${(b / 1024 / 1024).toFixed(1)} MB`;
}

export default function AreaCliente() {
    const [email, setEmail] = useState("");
    const [phone, setPhone] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [result, setResult] = useState(null);

    const submit = async (e) => {
        e?.preventDefault?.();
        setError(""); setLoading(true);
        try {
            const { data } = await api.post("/customer/lookup", { email, phone });
            setResult(data);
        } catch (err) { setError(formatApiError(err)); setResult(null); }
        setLoading(false);
    };

    const reset = () => { setResult(null); setError(""); };

    return (
        <div style={{ fontFamily: '"Plus Jakarta Sans", system-ui, sans-serif', background: "#F8FAFC", color: "#0A1730", minHeight: "100vh" }}>
            {/* Header */}
            <header className="px-6 md:px-12 py-4 flex items-center justify-between sticky top-0 z-40 backdrop-blur-md" style={{ background: "rgba(255,255,255,0.95)", borderBottom: "1px solid #E6ECFA" }}>
                <Link to="/totalpl" className="flex items-center gap-2 text-[13px] font-bold hover:opacity-70" data-testid="ac-back">
                    <ArrowLeft size={14} /> <span>Torna a TotalPL</span>
                </Link>
                <div className="flex items-center gap-2 px-3 py-1.5 rounded-full" style={{ background: "rgba(255,26,92,0.12)", color: "#FF1A5C" }}>
                    <Package size={13} />
                    <span className="mono text-[10.5px] uppercase tracking-[0.15em] font-extrabold">Area Cliente</span>
                </div>
                {result && (
                    <button type="button" onClick={reset} className="text-[12px] font-bold hover:opacity-70 inline-flex items-center gap-1" data-testid="ac-new-lookup">
                        Nuova ricerca
                    </button>
                )}
                {!result && <span className="w-[110px]" />}
            </header>

            {!result ? (
                <>
                    {/* Hero + Form */}
                    <section className="px-6 md:px-12 py-16 md:py-24" style={{ background: "linear-gradient(135deg, #FFF5F7 0%, #FFEEDC 100%)" }}>
                        <div className="max-w-2xl mx-auto text-center mb-10">
                            <h1 className="text-[36px] md:text-[52px] font-extrabold tracking-tighter leading-[1.05]">
                                I tuoi <span style={{ background: "linear-gradient(135deg, #FF1A5C, #FF6B1A)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>ordini di stampa</span> in un solo posto.
                            </h1>
                            <p className="mt-4 text-[16px]" style={{ color: "#5C6B8C" }}>
                                Vedi tutti i lavori che hai fatto presso le tipografie partner TotalPL. Inserisci la stessa <b>email</b> e lo stesso <b>numero di telefono</b> che usi con loro.
                            </p>
                        </div>
                        <form onSubmit={submit} className="max-w-md mx-auto bg-white rounded-2xl p-7 shadow-xl" data-testid="ac-form">
                            <div className="mb-4">
                                <label className="mono text-[10px] uppercase tracking-wider font-bold mb-1.5 block" style={{ color: "#5C6B8C" }}>Email</label>
                                <div className="relative">
                                    <Mail size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 opacity-40" />
                                    <input
                                        type="email"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        placeholder="la-tua@email.it"
                                        required
                                        className="w-full pl-10 pr-3 py-3 rounded-lg border text-[14px] outline-none focus:border-[#FF1A5C]"
                                        style={{ borderColor: "#E6ECFA" }}
                                        data-testid="ac-email"
                                    />
                                </div>
                            </div>
                            <div className="mb-5">
                                <label className="mono text-[10px] uppercase tracking-wider font-bold mb-1.5 block" style={{ color: "#5C6B8C" }}>Telefono</label>
                                <div className="relative">
                                    <Phone size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 opacity-40" />
                                    <input
                                        type="tel"
                                        value={phone}
                                        onChange={(e) => setPhone(e.target.value)}
                                        placeholder="+39 333 1234567"
                                        required
                                        className="w-full pl-10 pr-3 py-3 rounded-lg border text-[14px] outline-none focus:border-[#FF1A5C]"
                                        style={{ borderColor: "#E6ECFA" }}
                                        data-testid="ac-phone"
                                    />
                                </div>
                            </div>
                            {error && (
                                <div className="mb-4 p-3 rounded-lg text-[13px] flex items-start gap-2" style={{ background: "#FEF2F2", color: "#991B1B" }} data-testid="ac-error">
                                    <AlertCircle size={15} className="flex-shrink-0 mt-0.5" />
                                    <span>{error}</span>
                                </div>
                            )}
                            <button
                                type="submit"
                                disabled={loading}
                                className="w-full inline-flex items-center justify-center gap-2 py-3.5 rounded-lg font-extrabold text-[14px] text-white disabled:opacity-50"
                                style={{ background: "linear-gradient(135deg, #FF1A5C, #FF6B1A)", boxShadow: "0 12px 28px rgba(255,26,92,0.3)" }}
                                data-testid="ac-submit"
                            >
                                {loading ? "Ricerca in corso..." : (<><Search size={14} /> Cerca i miei ordini</>)}
                            </button>
                            <p className="mt-4 text-[11px] text-center opacity-60">
                                Sicurezza: serve match esatto email + telefono per accedere agli ordini. Nessuna registrazione richiesta.
                            </p>
                        </form>
                    </section>

                    {/* How it works */}
                    <section className="px-6 md:px-12 py-16">
                        <div className="max-w-4xl mx-auto">
                            <h2 className="text-[22px] md:text-[28px] font-extrabold tracking-tight mb-8 text-center">Come funziona</h2>
                            <div className="grid md:grid-cols-3 gap-4">
                                {[
                                    { n: 1, t: "Inserisci email e telefono", d: "Gli stessi che hai dato in tipografia quando hai fatto l'ordine." },
                                    { n: 2, t: "Vedi tutti i tuoi ordini", d: "Storico completo, stato attuale, file allegati, scadenze." },
                                    { n: 3, t: "Scarica i file", d: "Esecutivi, anteprime, fatture — sempre a portata di mano." },
                                ].map(s => (
                                    <div key={s.n} className="p-5 rounded-xl" style={{ background: "#fff", border: "1px solid #E6ECFA" }}>
                                        <div className="w-9 h-9 rounded-full grid place-items-center text-white font-extrabold text-[14px] mb-3" style={{ background: "linear-gradient(135deg, #FF1A5C, #FF6B1A)" }}>{s.n}</div>
                                        <h3 className="font-extrabold text-[15px] mb-1">{s.t}</h3>
                                        <p className="text-[13px] opacity-70">{s.d}</p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </section>
                </>
            ) : (
                <>
                    {/* RESULT — dashboard */}
                    <section className="px-6 md:px-12 py-10" style={{ background: "linear-gradient(135deg, #FFF5F7 0%, #FFEEDC 100%)" }}>
                        <div className="max-w-5xl mx-auto">
                            <h1 className="text-[28px] md:text-[36px] font-extrabold tracking-tighter">
                                Ciao <span style={{ background: "linear-gradient(135deg, #FF1A5C, #FF6B1A)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>{result.customer.name}</span>
                            </h1>
                            <p className="mt-1 text-[14px]" style={{ color: "#5C6B8C" }}>
                                {result.jobs_count} ordini trovati presso {result.tenants_count} {result.tenants_count === 1 ? "tipografia" : "tipografie"} TotalPL
                            </p>
                        </div>
                    </section>

                    <section className="px-6 md:px-12 py-10">
                        <div className="max-w-5xl mx-auto">
                            {result.jobs.length === 0 ? (
                                <div className="text-center py-16 opacity-60">Nessun ordine trovato.</div>
                            ) : (
                                <div className="space-y-3" data-testid="ac-orders-list">
                                    {result.jobs.map(j => {
                                        const stageColor = j.status?.color || "#94A3B8";
                                        const stageLabel = j.status?.name || STATUS_LABELS[j.status?.kind] || "—";
                                        const pri = PRIORITY_LABELS[j.priority] || PRIORITY_LABELS.normal;
                                        return (
                                            <div key={j.id} className="rounded-xl p-5 transition" style={{ background: "#fff", border: "1px solid #E6ECFA" }} data-testid={`ac-order-${j.id}`}>
                                                <div className="flex items-start justify-between flex-wrap gap-3 mb-3">
                                                    <div className="min-w-0">
                                                        <div className="flex items-center gap-2 mb-1">
                                                            <span className="mono text-[10px] uppercase tracking-wider font-bold opacity-60">#{j.year}-{String(j.job_number).padStart(4, "0")}</span>
                                                            <span className="text-[10px] mono uppercase tracking-wider font-bold px-2 py-0.5 rounded-full" style={{ background: `${stageColor}20`, color: stageColor }}>● {stageLabel}</span>
                                                            {j.priority && j.priority !== "normal" && (
                                                                <span className="text-[10px] mono uppercase tracking-wider font-bold px-2 py-0.5 rounded-full" style={{ background: `${pri.color}20`, color: pri.color }}>{pri.label}</span>
                                                            )}
                                                        </div>
                                                        <h3 className="text-[17px] font-extrabold">{j.title}</h3>
                                                        <div className="flex items-center gap-3 mt-1 text-[12px] opacity-70">
                                                            <span className="inline-flex items-center gap-1"><Building2 size={11} /> {j.tenant_name}</span>
                                                            {j.quantity && <span>· {j.quantity} pz</span>}
                                                            {j.deadline && <span className="inline-flex items-center gap-1">· <Calendar size={11} /> {fmtDate(j.deadline)}</span>}
                                                        </div>
                                                    </div>
                                                    {j.tracking_token && (
                                                        <a href={`/track/${j.tracking_token}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12px] font-extrabold" style={{ background: "rgba(255,26,92,0.1)", color: "#FF1A5C" }} data-testid={`ac-track-${j.id}`}>
                                                            Tracking <ExternalLink size={11} />
                                                        </a>
                                                    )}
                                                </div>
                                                {j.files && j.files.length > 0 && (
                                                    <div className="pt-3 mt-3 border-t" style={{ borderColor: "#F1F5F9" }}>
                                                        <div className="mono text-[10px] uppercase tracking-wider font-bold opacity-60 mb-2">{j.files.length} file allegati</div>
                                                        <div className="grid sm:grid-cols-2 gap-2">
                                                            {j.files.map((f, i) => (
                                                                <a key={i} href={f.url || "#"} download className="flex items-center gap-2.5 px-2.5 py-2 rounded-lg hover:bg-gray-50 transition" style={{ border: "1px solid #F1F5F9" }} data-testid={`ac-file-${j.id}-${i}`}>
                                                                    <div className="w-8 h-8 rounded-md grid place-items-center flex-shrink-0" style={{ background: "rgba(255,26,92,0.1)", color: "#FF1A5C" }}>
                                                                        <Download size={14} />
                                                                    </div>
                                                                    <div className="flex-1 min-w-0">
                                                                        <div className="text-[12.5px] font-bold truncate">{f.name}</div>
                                                                        <div className="text-[10.5px] opacity-50">{bytesToReadable(f.size)}</div>
                                                                    </div>
                                                                </a>
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                            <div className="text-center mt-8">
                                <button type="button" onClick={reset} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-[12.5px] font-bold opacity-60 hover:opacity-100" data-testid="ac-reset">
                                    <ArrowRight size={12} /> Cerca con altre credenziali
                                </button>
                            </div>
                        </div>
                    </section>
                </>
            )}
        </div>
    );
}
